import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Card, Flex } from "@mantine/core";
import { Alert, Spinner, Status, Tooltip } from "@snapchat/snap-design-system";
import { ChevronDownIcon, InformationIcon } from "@snapchat/snap-design-system-icons";

import { formatMediumDateUTC } from "../../camkit/common/formatters/formatDate";
import { type GoogleProtobufTimestamp, type PayoutPeriod, PayoutTimeGranularity } from "../../gql-generated/types";
import { usePayoutStatsHistory } from "../../lens-insights/hooks/usePayoutStatsHistory";
import { usePayoutStatsSummary } from "../../lens-insights/hooks/usePayoutStatsSummary";
import { clampLensPlusPayoutDateRange } from "../../lens-insights/utils/lensPlusDateRanges";
import { formatNullableNumber } from "../../lens-stats/formatters/formatNumbers";
import { formatMilliUsd, formatMonthName } from "../utils";

import styles from "./LensPayoutHistoryCard.module.css";
import {
    AllTime,
    LatestPayout,
    MonthPayout,
    NoPayoutIssued,
    PayoutDataError,
    PayoutFooterDisclaimer,
    PayoutHistory,
    TotalEarned,
    TotalLensPlusSubscriberActions,
    TotalLensPlusSubscriberActionsTooltip,
    TotalLensPlusSubscriberActionsTooltipMeta,
} from "./messages.generated";

const HISTORY_LIMIT = 50;

const formatFullDateUTC = (dateStr: string): string => formatMediumDateUTC(new Date(dateStr));

interface PayoutPeriodWithDate extends PayoutPeriod {
    readonly period: string;
}

interface LensPayoutHistoryCardProps {
    unlockableId: string;
    showSubscriberActions?: boolean;
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null;
}

export const LensPayoutHistoryCard: FC<LensPayoutHistoryCardProps> = ({
    unlockableId,
    showSubscriberActions = false,
    markedAsExclusiveAt,
}) => {
    const dateRange = clampLensPlusPayoutDateRange({}, markedAsExclusiveAt).range;
    const {
        data: summaryData,
        loading: summaryLoading,
        error: summaryError,
    } = usePayoutStatsSummary({
        countryFilters: [],
        unlockableId,
        ...dateRange,
    });

    const {
        periods,
        loading: historyLoading,
        error: historyError,
    } = usePayoutStatsHistory({
        countryFilters: [],
        granularity: PayoutTimeGranularity.TimeGranularityMonthly,
        limit: HISTORY_LIMIT,
        unlockableId,
        ...dateRange,
    });

    const sortedPeriods = useMemo<PayoutPeriodWithDate[]>(
        () =>
            periods
                .filter((p): p is PayoutPeriodWithDate => Boolean(p.period))
                .sort((a, b) => new Date(b.period).getTime() - new Date(a.period).getTime()),
        [periods],
    );

    const listRef = useRef<HTMLDivElement>(null);
    const [showFade, setShowFade] = useState(false);
    const [historyExpanded, setHistoryExpanded] = useState(true);

    const updateFade = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const isScrollable = el.scrollHeight > el.clientHeight;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4;
        setShowFade(isScrollable && !atBottom);
    }, []);

    useEffect(() => {
        updateFade();
    }, [updateFade, sortedPeriods, historyExpanded]);

    const historyRows = useMemo(
        () =>
            sortedPeriods.map((period) => (
                <div key={period.period} className={styles.historyRow}>
                    <div className={styles.historyRowLeft}>
                        <div className={styles.historyDot} />
                        <div className={styles.historyRowInfo}>
                            <span className={styles.historyDate}>{formatFullDateUTC(period.period)}</span>
                            <span className={styles.historyLabel}>
                                <MonthPayout month={formatMonthName(period.period)} />
                            </span>
                        </div>
                    </div>
                    <span className={styles.historyAmount}>{formatMilliUsd(period.totalAmountMilliUsd)}</span>
                </div>
            )),
        [sortedPeriods],
    );

    const loading = summaryLoading || historyLoading;
    const error = summaryError ?? historyError;

    if (loading) {
        return <Spinner />;
    }

    if (error) {
        return (
            <Card shadow="sm" radius="md">
                <Alert status={Status.ERROR} title={<PayoutDataError />} />
            </Card>
        );
    }

    const totalEarnings = Number(summaryData?.totalEarningsMilliUsd ?? 0);
    const lastPayoutAt = summaryData?.lastPayoutAt;
    const hasPayouts = sortedPeriods.length > 0;

    if (totalEarnings === 0 && !hasPayouts) {
        return (
            <Card shadow="sm" radius="md">
                <div className={styles.noPayoutText}>
                    <NoPayoutIssued />
                </div>
            </Card>
        );
    }

    const latestPeriod = sortedPeriods[0];

    return (
        <Card shadow="sm" radius="md">
            <Flex direction="column" gap="md">
                {/* Summary Row */}
                <div className={styles.summaryRow}>
                    <div className={styles.summaryBox}>
                        <span className={styles.summaryLabel}>
                            <LatestPayout />
                        </span>
                        <div className={styles.summaryValue}>
                            {latestPeriod ? formatMilliUsd(latestPeriod.totalAmountMilliUsd) : formatMilliUsd(0)}
                        </div>
                        <span className={styles.summarySubtext}>
                            {lastPayoutAt ? formatFullDateUTC(lastPayoutAt) : ""}
                        </span>
                    </div>
                    <div className={styles.summaryBox}>
                        <span className={styles.summaryLabel}>
                            <TotalEarned />
                        </span>
                        <div className={styles.summaryValue}>{formatMilliUsd(totalEarnings)}</div>
                        <span className={styles.summarySubtext}>
                            <AllTime />
                        </span>
                    </div>
                    {showSubscriberActions && (
                        <div className={styles.summaryBox}>
                            <span className={styles.summaryLabel}>
                                <TotalLensPlusSubscriberActions />
                                <Tooltip
                                    content={
                                        <>
                                            <TotalLensPlusSubscriberActionsTooltip />
                                            <br />
                                            <span style={{ opacity: 0.7 }}>
                                                <TotalLensPlusSubscriberActionsTooltipMeta />
                                            </span>
                                        </>
                                    }
                                >
                                    <InformationIcon />
                                </Tooltip>
                            </span>
                            <div className={styles.summaryValue}>
                                {formatNullableNumber(summaryData?.totalSubscriberUniqueUsers)}
                            </div>
                            <span className={styles.summarySubtext}>
                                <AllTime />
                            </span>
                        </div>
                    )}
                </div>

                {/* Payout History */}
                {hasPayouts && (
                    <div className={styles.historySection}>
                        <button
                            type="button"
                            className={styles.historyToggle}
                            aria-expanded={historyExpanded}
                            onClick={() => {
                                setHistoryExpanded((prev) => !prev);
                            }}
                        >
                            <span className={styles.historyHeader}>
                                <PayoutHistory />
                            </span>
                            <ChevronDownIcon
                                className={`${styles.historyChevron} ${historyExpanded ? styles.historyChevronExpanded : ""}`}
                            />
                        </button>
                        {historyExpanded && (
                            <div className={styles.historyListWrapper}>
                                <div ref={listRef} className={styles.historyList} onScroll={updateFade}>
                                    {historyRows}
                                </div>
                                {showFade && <div className={styles.historyFadeOverlay} />}
                            </div>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className={styles.footer}>
                    <PayoutFooterDisclaimer />
                </div>
            </Flex>
        </Card>
    );
};
